'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { translate } from '@/lib/i18n';
import type { CountryCode } from '@/lib/countries';
import { countryProfiles } from '@/lib/countries';

function CountryChip({ code }: { code: string }) {
  return (
    <span
      className="flex-shrink-0 flex items-center justify-center rounded-md font-bold"
      style={{
        width: '26px',
        height: '26px',
        backgroundColor: 'var(--primary)',
        color: 'var(--accent)',
        fontSize: '10px',
        borderRadius: '6px',
      }}
    >
      {code}
    </span>
  );
}

export default function CountrySwitcher() {
  const { code, profile, countryProfiles: cpList, setCountry } = useCountry();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  function handleSelect(selectedCode: CountryCode) {
    setCountry(selectedCode);
    setOpen(false);
    const selected = countryProfiles[selectedCode];
    if (selected) {
      // Toast in the target country's official language
      const targetLang = selected.officialLanguage;
      toast.success(
        translate(targetLang, 'toast.countrySwitched', {
          name: selected.name,
          utility: selected.utility,
        })
      );
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors hover:bg-surface-light"
        style={{
          borderColor: 'var(--border)',
          borderRadius: '8px',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <CountryChip code={code} />
        <span
          className="text-[13px] font-medium hidden sm:block"
          style={{ color: 'var(--foreground)' }}
        >
          {profile.name}
        </span>
        <ChevronDown
          size={14}
          style={{ color: 'var(--muted-foreground)' }}
          className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute right-0 mt-1 z-50 rounded-xl border shadow-lg overflow-hidden"
          style={{
            width: '300px',
            backgroundColor: 'var(--card)',
            borderColor: 'var(--border)',
            borderRadius: '12px',
            padding: '4px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
          role="listbox"
        >
          {cpList.map((cp) => {
            const isSelected = cp.code === code;
            return (
              <button
                key={cp.code}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(cp.code as CountryCode)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-surface-light text-left"
                style={
                  isSelected
                    ? { backgroundColor: 'rgba(201,151,58,0.10)' }
                    : {}
                }
              >
                <CountryChip code={cp.code} />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[13px] font-medium leading-tight truncate"
                    style={{ color: 'var(--foreground)' }}
                  >
                    {cp.name}
                  </p>
                  <p
                    className="text-[11px] leading-tight truncate mt-0.5"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    {cp.utility}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="text-[12px] font-semibold tabular-nums"
                    style={{ color: 'var(--foreground)' }}
                  >
                    {cp.accessPercent}%
                  </span>
                  {isSelected && (
                    <Check size={14} style={{ color: 'var(--accent)' }} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
